import { WbTextField } from '@agilelab/plugin-wb-platform';
import {
  useWittyAutocomplete,
  useWittyTable,
  useWittyTableRow,
  WittyInputAdornment,
  WittyNoContextTooltip,
} from '@agilelab/plugin-wb-witty-react';
import { useCallback, useEffect, useMemo } from 'react';
import { extractCustomProperties, isHidden } from '../../utils';
import { WittyTextPickerProps } from './schema';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

export const WittyTextPicker = (props: WittyTextPickerProps) => {
  const {
    id,
    disabled,
    hidden,
    required,
    rawErrors = [],
    rawDescription,
    schema,
    uiSchema,
    formData,
    onChange,
    idSchema,
    formContext,
  } = props;

  const customProps = extractCustomProperties(uiSchema);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const rjsfId = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(rjsfId, rootSchema);
  }, [idSchema, rootSchema]);

  // Handle changes with dependency manager support
  const handleChange = useCallback(
    (value: string) => {
      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  const witty = useWittyAutocomplete({
    fieldId: idSchema.$id,
    schema: { ...schema, ...uiSchema },
    onChange,
  });
  const fieldId = useMemo(() => `${idSchema.$id}`, [idSchema.$id]);

  const wittyTable = useWittyTable();
  const wittyTableRow = useWittyTableRow();
  const canAutocomplete = wittyTableRow?.canAutocomplete;
  const updateWittyFields = wittyTable?.updateWittyFields;
  const triggeringColumns = wittyTable?.triggeringColumns ?? [];

  const { debounceMs } = useFormExtraConfigContext();

  useEffect(() => {
    if (updateWittyFields && witty.enabled && canAutocomplete) {
      updateWittyFields({
        type: 'ADD_FIELD',
        payload: {
          fieldId: fieldId,
          type: 'text',
        },
      });
    }

    return () => {
      updateWittyFields?.({ type: 'REMOVE_FIELD', payload: fieldId });
    };
  }, [canAutocomplete, fieldId, onChange, updateWittyFields, witty.enabled]);

  if (hidden) {
    return <></>;
  }

  return (
    <WbTextField
      id={id}
      style={{
        display: isHidden(uiSchema) ? 'none' : undefined,
      }}
      debounceMs={debounceMs}
      showValueTooltipWhenOverflow
      label={schema.title}
      placeholder={uiSchema ? uiSchema['ui:placeholder'] : undefined}
      helperText={rawDescription}
      disabled={disabled || witty.loading}
      required={required}
      value={formData ?? ''}
      onChange={({ target: { value } }) => handleChange(value)}
      error={rawErrors?.length > 0 && !formData}
      {...customProps}
      InputProps={{
        ...customProps.InputProps,
        startAdornment: witty.enabled && (
          <WittyNoContextTooltip
            triggeringColumns={triggeringColumns}
            hide={canAutocomplete}
          >
            <span>
              <WittyInputAdornment
                disabled={!canAutocomplete}
                loading={witty.loading}
                onClick={witty.suggestText}
              />
            </span>
          </WittyNoContextTooltip>
        ),
      }}
    />
  );
};
